import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BackofficeLayout from '../../components/backoffice/BackofficeLayout';
import DataTable from '../../components/backoffice/DataTable';
import ConfirmDialog from '../../components/backoffice/ConfirmDialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { propertyService } from '../../services/propertyService';
import { Plus, Pencil, Trash2, Eye, MapPin, Search, Star, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const Properties = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, propertyId: null });
  const [filters, setFilters] = useState({
    search: '',
    typeBien: 'all',
    statut: 'all',
    actif: 'all'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  useEffect(() => {
    loadProperties();
  }, [filters, pagination.page]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const queryFilters = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (filters.search) queryFilters.search = filters.search;
      if (filters.typeBien !== 'all') queryFilters.typeBien = filters.typeBien;
      if (filters.statut !== 'all') queryFilters.statut = filters.statut;
      if (filters.actif !== 'all') queryFilters.actif = filters.actif === 'true';

      const response = await propertyService.getAllProperties(queryFilters);
      setProperties(response.data || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (error) {
      console.error('Error loading properties:', error);
      toast.error('Erreur lors du chargement des biens');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await propertyService.deleteProperty(deleteDialog.propertyId);
      toast.success('Bien supprimé avec succès');
      setDeleteDialog({ open: false, propertyId: null });
      loadProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression du bien');
    }
  };

  const handleToggleVedette = async (id) => {
    try {
      await propertyService.toggleVedette(id);
      toast.success('Statut vedette modifié');
      loadProperties();
    } catch (error) {
      console.error('Error toggling vedette:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la modification du statut vedette');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getStatusBadge = (statut) => {
    const config = {
      'Disponible': { label: 'Disponible', className: 'bg-green-100 text-green-800' },
      'En cours': { label: 'En cours', className: 'bg-yellow-100 text-yellow-800' },
      'Vendu': { label: 'Vendu', className: 'bg-blue-100 text-blue-800' },
      'Loué': { label: 'Loué', className: 'bg-purple-100 text-purple-800' },
      'Réservé': { label: 'Réservé', className: 'bg-orange-100 text-orange-800' }
    };
    const { label, className } = config[statut] || { label: statut || 'Inconnu', className: 'bg-slate-100 text-slate-800' };
    return <Badge className={className}>{label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy', { locale: fr });
    } catch {
      return '-';
    }
  };

  const columns = [
    {
      header: 'Bien',
      cell: (row) => (
        <div className="flex items-center space-x-3">
          <img
            src={row.images?.[0]?.urlImage || '/placeholder.jpg'}
            alt={row.titre}
            className="w-16 h-16 object-cover rounded-lg"
          />
          <div className="max-w-xs">
            <div className="font-medium line-clamp-1">{row.titre}</div>
            <div className="text-sm text-slate-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span className="line-clamp-1">{row.localisation?.zone || row.localisation?.ville || '-'}</span>
            </div>
          </div>
        </div>
      )
    },
    {
      header: 'Type',
      accessorKey: 'typeBien',
      cell: (row) => <span className="capitalize">{row.typeBien}</span>
    },
    {
      header: 'Prix',
      cell: (row) => (
        <div className="font-semibold">
          {row.prixSurDemande
            ? 'Sur demande'
            : row.prix
              ? `${parseInt(row.prix).toLocaleString()} ${row.devise || 'FCFA'}`
              : 'Sur demande'
          }
        </div>
      )
    },
    {
      header: 'Statut',
      accessorKey: 'statut',
      cell: (row) => getStatusBadge(row.statut)
    },
    {
      header: 'Vedette',
      cell: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleVedette(row.id);
          }}
          className="p-1 hover:bg-amber-50 rounded transition-colors"
          title={row.enVedette ? 'Retirer de la vedette' : 'Mettre en vedette'}
        >
          <Star className={`w-5 h-5 ${row.enVedette ? 'fill-amber-500 text-amber-500' : 'text-slate-300'}`} />
        </button>
      )
    },
    {
      header: 'Actif',
      accessorKey: 'actif',
      cell: (row) => (
        <Badge className={row.actif ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>
          {row.actif ? 'Oui' : 'Non'}
        </Badge>
      )
    },
    {
      header: 'Date',
      accessorKey: 'dateCreation',
      cell: (row) => (
        <span className="text-sm text-slate-600">
          {formatDate(row.dateCreation)}
        </span>
      )
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/property/${row.id}`)}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/backoffice/properties/edit/${row.id}`)}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteDialog({ open: true, propertyId: row.id })}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
      className: 'text-right'
    }
  ];

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/backoffice')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Gestion des Biens</h1>
              <p className="text-slate-600 mt-1">
                {pagination.total} bien{pagination.total > 1 ? 's' : ''} au total
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/backoffice/properties/new')}
            className="bg-amber-600 hover:bg-amber-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un bien
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-9"
                />
              </div>

              <Select value={filters.typeBien} onValueChange={(value) => handleFilterChange('typeBien', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Type de bien" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="Villa">Villa</SelectItem>
                  <SelectItem value="Appartement">Appartement</SelectItem>
                  <SelectItem value="Terrain">Terrain</SelectItem>
                  <SelectItem value="Immeuble">Immeuble</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.statut} onValueChange={(value) => handleFilterChange('statut', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="Disponible">Disponible</SelectItem>
                  <SelectItem value="En cours">En cours</SelectItem>
                  <SelectItem value="Vendu">Vendu</SelectItem>
                  <SelectItem value="Loué">Loué</SelectItem>
                  <SelectItem value="Réservé">Réservé</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.actif} onValueChange={(value) => handleFilterChange('actif', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Actif" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="true">Actif uniquement</SelectItem>
                  <SelectItem value="false">Inactif uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <DataTable
          columns={columns}
          data={properties}
          loading={loading}
          emptyMessage="Aucun bien trouvé"
        />

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Page {pagination.page} sur {Math.ceil(pagination.total / pagination.limit)}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, propertyId: null })}
        title="Supprimer le bien"
        description="Êtes-vous sûr de vouloir supprimer ce bien ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        onConfirm={handleDelete}
        variant="destructive"
      />
    </BackofficeLayout>
  );
};

export default Properties;
